/**
 * Admin Price Plans — configure client-facing plan prices (Monthly,
 * Yearly, Lifetime) shown on /activation, and reseller credit-pack
 * pricing shown to resellers in "Buy Credits".
 */
import React, { useEffect, useState } from "react";
import { getPricePlans, updatePricePlans } from "../lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Tag,
  Save,
  RefreshCw,
  Plus,
  Trash2,
  DollarSign,
  Users as UsersIcon,
} from "lucide-react";

const rndId = () => Math.random().toString(36).slice(2, 12);

export default function PricePlans() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPricePlans();
      setCfg(data);
      setInitial(data);
    } catch (e) {
      toast({
        title: "Failed to load price plans",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty = cfg && initial && JSON.stringify(cfg) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updatePricePlans(cfg);
      setCfg(updated);
      setInitial(updated);
      toast({
        title: "Prices saved",
        description: "The Activation page and reseller Buy-Credits page now reflect the new prices.",
      });
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateClient = (id, patch) =>
    setCfg((c) => ({
      ...c,
      client_plans: c.client_plans.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }));

  const updatePack = (id, patch) =>
    setCfg((c) => ({
      ...c,
      reseller_packs: c.reseller_packs.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }));

  const addPack = () =>
    setCfg((c) => ({
      ...c,
      reseller_packs: [
        ...c.reseller_packs,
        {
          id: rndId(),
          name: "New pack",
          credits: 1,
          price_display: "$0",
          price_hint: "",
          enabled: true,
        },
      ],
    }));

  const removePack = (id) =>
    setCfg((c) => ({ ...c, reseller_packs: c.reseller_packs.filter((p) => p.id !== id) }));

  if (loading || !cfg) {
    return (
      <div className="p-6 text-neutral-500" data-testid="price-plans-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-price-plans-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            <Tag className="h-7 w-7 text-emerald-400" />
            Price plans
          </h1>
          <p className="max-w-2xl text-sm text-neutral-400">
            Client-facing plan prices appear on{" "}
            <a href="/activation" target="_blank" rel="noreferrer" className="text-emerald-400 underline">/activation</a>.
            Reseller credit-pack prices are shown to resellers in the &quot;Buy Credits&quot; page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={saving}
            data-testid="price-plans-reload"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Reload
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="price-plans-save"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Client plans */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <DollarSign className="h-5 w-5 text-emerald-400" /> Client plans
          </CardTitle>
          <CardDescription className="text-xs text-neutral-500">
            One-device activation prices shown to end-users on the public Activation page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          {cfg.client_plans.map((p) => (
            <div
              key={p.id}
              className="grid gap-3 rounded-xl border border-white/5 bg-black/40 p-4 md:grid-cols-[80px_1fr_120px_1fr_100px]"
              data-testid={`client-plan-${p.id}`}
            >
              <div className="flex items-center">
                <span className="text-xs uppercase tracking-widest text-neutral-500">
                  {p.id}
                </span>
              </div>
              <div>
                <Label className="text-[10px] text-neutral-500">Name</Label>
                <Input
                  value={p.name}
                  onChange={(e) => updateClient(p.id, { name: e.target.value })}
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid={`client-plan-${p.id}-name`}
                />
              </div>
              <div>
                <Label className="text-[10px] text-neutral-500">Price</Label>
                <Input
                  value={p.price_display}
                  onChange={(e) => updateClient(p.id, { price_display: e.target.value })}
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid={`client-plan-${p.id}-price`}
                />
              </div>
              <div>
                <Label className="text-[10px] text-neutral-500">Sub-label</Label>
                <Input
                  value={p.price_hint || ""}
                  onChange={(e) => updateClient(p.id, { price_hint: e.target.value })}
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid={`client-plan-${p.id}-hint`}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Label className="text-xs">Enabled</Label>
                <Switch
                  checked={!!p.enabled}
                  onCheckedChange={(v) => updateClient(p.id, { enabled: !!v })}
                  data-testid={`client-plan-${p.id}-enabled`}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Reseller packs */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <UsersIcon className="h-5 w-5 text-emerald-400" /> Reseller credit packs
            </CardTitle>
            <CardDescription className="text-xs text-neutral-500">
              Bulk packs resellers can buy to top up their key-generation credits.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={addPack}
            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
            data-testid="add-reseller-pack"
          >
            <Plus className="mr-2 h-4 w-4" /> Add pack
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          {cfg.reseller_packs.length === 0 && (
            <p className="text-sm text-neutral-500">No packs configured yet.</p>
          )}
          {cfg.reseller_packs.map((p) => (
            <div
              key={p.id}
              className="grid gap-3 rounded-xl border border-white/5 bg-black/40 p-4 md:grid-cols-[1fr_100px_120px_1fr_auto_auto]"
              data-testid={`reseller-pack-${p.id}`}
            >
              <div>
                <Label className="text-[10px] text-neutral-500">Name</Label>
                <Input
                  value={p.name}
                  onChange={(e) => updatePack(p.id, { name: e.target.value })}
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid={`reseller-pack-${p.id}-name`}
                />
              </div>
              <div>
                <Label className="text-[10px] text-neutral-500">Credits</Label>
                <Input
                  type="number"
                  min="1"
                  value={p.credits}
                  onChange={(e) => updatePack(p.id, { credits: parseInt(e.target.value || "0", 10) })}
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid={`reseller-pack-${p.id}-credits`}
                />
              </div>
              <div>
                <Label className="text-[10px] text-neutral-500">Price</Label>
                <Input
                  value={p.price_display}
                  onChange={(e) => updatePack(p.id, { price_display: e.target.value })}
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid={`reseller-pack-${p.id}-price`}
                />
              </div>
              <div>
                <Label className="text-[10px] text-neutral-500">Sub-label</Label>
                <Input
                  value={p.price_hint || ""}
                  onChange={(e) => updatePack(p.id, { price_hint: e.target.value })}
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid={`reseller-pack-${p.id}-hint`}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Label className="text-xs">Enabled</Label>
                <Switch
                  checked={!!p.enabled}
                  onCheckedChange={(v) => updatePack(p.id, { enabled: !!v })}
                  data-testid={`reseller-pack-${p.id}-enabled`}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => removePack(p.id)}
                className="border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/10"
                data-testid={`reseller-pack-${p.id}-delete`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
